import React, { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { deleteDigitalAsset } from "../../../services/api/digitalAsset";
import { useMutation } from "@tanstack/react-query";
import AddEditVerifyIdentityModal from "./components/AddEditVerifyIdentityModal";
import AdminEditAddressModal from "./components/AdminEditAddressModal";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { UserType } from "../../../utils/enum";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import useAuth from "../../../hooks/useAuth";

// Stored as an upper snake case enum, e.g. SOUTH_AFRICAN -> "South African".
const formatNationality = (value) =>
  value
    ?.split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

const AddEditVerifyIdentity = ({
  readOnly = false,
  identityDocuments,
  personalAddress,
  nationality,
  refetchMe,
  authRole,
  // Whose profile this card shows; an ADMIN edits this user's home address.
  userId,
  // Approved profiles keep their nationality and ID documents; only support can change them.
  identityLocked = false,
}) => {
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const [isAddEditVerifyIdentityOpen, setIsAddEditVerifyIdentity] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isAdminAddressOpen, setIsAdminAddressOpen] = useState(false);
  // Only the user themselves and an ADMIN may change the address (the backend
  // enforces the same rule). An engineer edits the whole card, but only on their
  // own profile; an admin may edit only the address, on anyone's.
  const isSelf = userId == null || Number(userId) === Number(auth?.user?.id);
  const canEditOwn = !readOnly && authRole === UserType.FREELANCER && isSelf;
  const canAdminEditAddress = !readOnly && authRole === UserType.ADMIN;
  const {
    isPending: isDeletedDigitalAssetApiLoading,
    error: isDeletedDigitalAssetApiError,
    data: deletedDigitalAsset,
    mutateAsync: deleteDigitalAssetMutation,
  } = useMutation({
    mutationFn: deleteDigitalAsset,
    enabled: false,
  });

  const handleDocumentDelete = async () => {
    if (!documentToDelete) return;
    try {
      await deleteDigitalAssetMutation(documentToDelete.id);
      setDocumentToDelete(null);
      dispatch(showToast({ message: "Document deleted successfully", type: "success" }));
      refetchMe();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to delete document",
          type: "error",
        }),
      );
    }
  };

  const row = (label, value, isLast = false) => (
    <div
      className={`grid grid-cols-1 md:grid-cols-12 gap-1 items-center pb-2 pt-4 ${
        isLast ? "" : "border-b"
      }`}
    >
      <p className="font-normal md:col-span-2 text-sm">{label}</p>
      <div className="md:col-span-10">{value}</div>
    </div>
  );

  return (
    <BaseCard className="py-2 px-4 sm:px-6 rounded-lg" elevation={1}>
      <div className="bg-light px-4 py-2 mt-2 mb-2 rounded-md flex justify-between items-center">
        <h6 className="text-dark font-normal">Verify your Identity</h6>
        {canEditOwn && (
          <i
            className="fa-light fa-pencil text-gray-900 cursor-pointer"
            onClick={() => setIsAddEditVerifyIdentity(true)}
          ></i>
        )}
        {canAdminEditAddress && (
          <i
            className="fa-light fa-pencil text-gray-900 cursor-pointer"
            title="Edit home address"
            onClick={() => setIsAdminAddressOpen(true)}
          ></i>
        )}
      </div>

      <div className="px-4">
        {row("Nationality", <p className="text-sm">{formatNationality(nationality) || "-"}</p>)}

        {row(
          "Attachments",
          identityDocuments?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {identityDocuments.map((document) => (
                <div
                  key={document?.id}
                  className="flex items-center justify-between gap-3 min-w-0 bg-light rounded-md py-3 px-4 cursor-default"
                >
                  <div className="flex items-center min-w-0">
                    <i className="fa-regular fa-file text-xl text-red-500 shrink-0"></i>
                    <p className="ml-3 truncate">{document?.filename}</p>
                  </div>

                  <div className="flex items-center shrink-0">
                    {document?.url && (
                      <a
                        href={document.url}
                        download
                        target="_blank"
                        type="application/octet-stream"
                      >
                        <i className="fa-solid fa-download mr-3 text-emerald-700 text-base cursor-pointer"></i>
                      </a>
                    )}

                    {!readOnly && authRole === UserType.FREELANCER && !identityLocked && (
                      <i
                        className="fa-regular fa-trash-can text-red-600 cursor-pointer"
                        onClick={() => setDocumentToDelete(document)}
                      ></i>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm">-</p>
          ),
        )}

        {row(
          "Home Address",
          <p className="text-sm">{personalAddress?.addressLine1 || "-"}</p>,
          true,
        )}
      </div>
      {documentToDelete && (
        <BaseConfirmModal
          open={!!documentToDelete}
          onClose={() => setDocumentToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Document"
          description={`Are you sure you want to delete "${documentToDelete?.filename}"? This action cannot be undone.`}
          isLoading={isDeletedDigitalAssetApiLoading}
          onConfirm={handleDocumentDelete}
        />
      )}

      {isAdminAddressOpen && canAdminEditAddress && (
        <AdminEditAddressModal
          open={isAdminAddressOpen}
          close={setIsAdminAddressOpen}
          userId={userId}
          personalAddress={personalAddress}
          refetchMe={refetchMe}
        />
      )}

      {isAddEditVerifyIdentityOpen && canEditOwn && (
        <AddEditVerifyIdentityModal
          open={isAddEditVerifyIdentityOpen}
          close={setIsAddEditVerifyIdentity}
          identityDocuments={identityDocuments}
          refetchMe={refetchMe}
          personalAddress={personalAddress}
        />
      )}
    </BaseCard>
  );
};

export default AddEditVerifyIdentity;
